import React, { useState, useEffect } from 'react';
import '../scss/Home.scss';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, Play } from 'lucide-react';
import {
    KanbanIcon,
    ListIcon,
    TeamIcon,
    SlackIcon,
    NotificationIcon,
    ImportIcon,
    TwitterIcon,
    LinkedInIcon,
    GitHubIcon
} from '../../../components/LandingPage/FeatureIcon';
import { useScrollAnimation, ScrollToTop } from '../../../components/LandingPage/ScrollAnimation';
import { useTheme } from '../../../context/ThemeContext';
import Toggle from '../../../components/Global/Toggle';
import heroScreenshot from '../../../assets/images/hero-screenshot.png';
import { StaticHeader, StaticFooter } from '../components';

// Pricing plans data from Billing.js
const plans = [
    {
        name: 'Free',
        description: 'Perfect for individuals and small teams getting started with Zoobbe.',
        prices: { monthly: '$0', annually: '$0' },
        subTexts: { monthly: 'Free forever', annually: 'Free forever' },
        features: [
            '15 collaborators',
            'Up to 15 boards',
            '512 MB storage per workspace',
            'File attachments: 20 MB per file',
            'Basic checklists',
            'Custom backgrounds & stickers',
            'Real-time notifications'
        ]
    },
    {
        name: 'Standard',
        description: 'More power for growing teams: organize, customize, and collaborate better.',
        prices: { monthly: '$4.99', annually: '$49.99' },
        subTexts: { monthly: 'Per seat / Month', annually: 'Per seat / Year' },
        features: [
            'Unlimited collaborators',
            'Unlimited boards & cards',
            'Unlimited storage',
            'File attachments: 250 MB per file',
            'Advanced checklists',
            'Saved searches & list colors',
            'Team member management',
            'Pro verified badge'
        ]
    },
    {
        name: 'Premium',
        description: 'For teams that need insights, controls, and more collaboration power.',
        prices: { monthly: '$9.99', annually: '$95.99' },
        subTexts: { monthly: 'Per seat / Month', annually: 'Per seat / Year' },
        popular: true,
        features: [
            'Everything in Standard',
            'Data export (JSON/CSV)',
            'Extended activity logs',
            'Performance insights',
            'Custom stickers & emojis',
            'White label branding',
            'Custom domain support',
            'Priority support'
        ]
    },
    {
        name: 'Enterprise',
        description: 'Enterprise-grade security, support, and scale. Tailored for large organizations.',
        prices: { monthly: 'Contact Sales', annually: 'Contact Sales' },
        subTexts: { monthly: 'Billing varies with number of users', annually: 'Billing varies with number of users' },
        features: [
            'Everything in Premium',
            'Advanced reporting',
            'User role management',
            'Organization-wide permissions',
            'SSO (Coming Soon)',
            'Custom security policies',
            'Dedicated support contact',
            'Enterprise-grade security'
        ]
    }
];

const Home = () => {
    // Initialize scroll animations
    useScrollAnimation();

    const navigate = useNavigate();
    const { theme } = useTheme();

    // Detect system theme locally for landing page (works without login)
    const [systemTheme, setSystemTheme] = useState('light');
    useEffect(() => {
        const updateSystemTheme = () => {
            const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setSystemTheme(isDarkMode ? 'dark' : 'light');
        };
        // Initial check
        updateSystemTheme();
        // Listen for changes
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', updateSystemTheme);
        return () => mediaQuery.removeEventListener('change', updateSystemTheme);
    }, []);

    // Pricing toggle state
    const [billingPeriod, setBillingPeriod] = useState('monthly');

    // Email signup state
    const [email, setEmail] = useState('');

    // Video popup state
    const [isVideoOpen, setIsVideoOpen] = useState(false);

    // Determine current theme
    const currentTheme = theme === 'system' ? systemTheme : theme;

    // Handle email signup
    const handleEmailSignup = (e) => {
        e.preventDefault();
        if (email) {
            // Navigate to signup page with email pre-filled
            navigate('/signup', { state: { email } });
        }
    };

    // Handle video popup
    const openVideo = () => setIsVideoOpen(true);
    const closeVideo = () => setIsVideoOpen(false);

    // Close video on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isVideoOpen) {
                closeVideo();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isVideoOpen]);

    // Scroll to top on page load/navigation
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Handle Help Center click - Open Crisp chat
    const handleHelpCenterClick = (e) => {
        e.preventDefault();
        if (window.$crisp) {
            window.$crisp.push(['do', 'chat:open']);
        }
    };

    return (
        <div className="landing-page">
            <ScrollToTop />

            {/* Navigation Header */}
            <StaticHeader variant="home" />

            {/* Hero Section */}
            <section className="hero">
                <div className="hero__container">
                    <div className="hero__content">
                        <div className="hero__text">
                            <h1 className="hero__title">
                                The Future of Teamwork Lives in <span className="hero__title-accent">Zoobbe</span>
                            </h1>
                            <p className="hero__subtitle">
                                Streamline your workflow with powerful boards, seamless collaboration, and intelligent automation. Where productivity meets simplicity.
                            </p>
                            <form className="hero__signup-form" onSubmit={handleEmailSignup}>
                                <div className="hero__signup-input-group">
                                    <input
                                        type="email"
                                        placeholder="Enter your email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="hero__email-input"
                                        required
                                    />
                                    <button type="submit" className="hero__signup-btn">
                                        Sign Up Free
                                    </button>
                                </div>
                            </form>
                            <div className="hero__social-proof">
                                <p className="hero__social-text">Trusted by teams worldwide</p>
                            </div>
                            {/* <p className="hero__note">Free forever. No credit card required.</p> */}
                        </div>
                        <div className="hero__image" onClick={openVideo}>
                            <img src={heroScreenshot} alt="Zoobbe Dashboard Screenshot" className="hero__screenshot" loading="lazy" />
                            <div className="hero__play-overlay">
                                <button type="button" className="hero__play-button" aria-label="Watch demo video">
                                    <Play className="hero__play-icon" size={32} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Black Friday Banner */}
            <section className="black-friday-banner">
                <div className="container">
                    <div className="bf-banner-content">
                        <div className="bf-banner-icon">🔥</div>
                        <div className="bf-banner-text">
                            <h3>DealMirror Exclusive: Lifetime Access to Zoobbe</h3>
                            <p>Get up to 50% OFF on all tiers. One-time payment, lifetime access. Limited time only!</p>
                        </div>
                        <Link to="/dealmirror" className="bf-banner-cta">
                            View Deals
                        </Link>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="features">
                <div className="container">
                    <div className="section-header">
                        <h2>Everything you need to stay organized</h2>
                        <p>Powerful features designed for modern teams</p>
                    </div>
                    <div className="features-grid">
                        <div className="feature-card">
                            <div className="feature-icon">
                                <KanbanIcon />
                            </div>
                            <h3>Kanban Boards</h3>
                            <p>Visualize your workflow with intuitive drag-and-drop boards that adapt to any project.</p>
                        </div>
                        {/* <div className="feature-card">
                            <div className="feature-icon">
                                <ListIcon />
                            </div>
                            <h3>List View</h3>
                            <p>Switch to detailed list views for comprehensive project tracking and data analysis.</p>
                        </div> */}
                        <div className="feature-card">
                            <div className="feature-icon">
                                <TeamIcon />
                            </div>
                            <h3>Team Workspaces</h3>
                            <p>Organize projects in dedicated workspaces with granular permissions and team management.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <SlackIcon />
                            </div>
                            <h3>Slack Integration</h3>
                            <p>Stay connected with seamless Slack integration for real-time updates and notifications.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <NotificationIcon />
                            </div>
                            <h3>Smart Notifications</h3>
                            <p>Get notified about what matters most with intelligent, customizable notification settings.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <ImportIcon />
                            </div>
                            <h3>Trello Import</h3>
                            <p>Migrate from Trello effortlessly with our one-click import tool. Keep your workflow intact.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it Works Section */}
            <section id="how-it-works" className="how-it-works">
                <div className="container">
                    <div className="section-header">
                        <h2>How Zoobbe Works</h2>
                        <p>Get started in minutes, not hours</p>
                    </div>
                    <div className="steps-grid">
                        <div className="step">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h3>Create Your Board</h3>
                                <p>Start with a template or build from scratch. Customize columns, labels, and workflows to match your team's needs.</p>
                            </div>
                        </div>
                        <div className="step">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h3>Invite Your Team</h3>
                                <p>Add team members, set permissions, and create workspaces. Collaboration starts with the right people in the right place.</p>
                            </div>
                        </div>
                        <div className="step">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h3>Stay Organized</h3>
                                <p>Track progress, manage deadlines, and keep everyone aligned with real-time updates and smart notifications.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="pricing">
                <div className="container">
                    <div className="section-header">
                        <h2>Simple, Transparent Pricing</h2>
                        <p>Choose the plan that fits your team's needs</p>
                    </div>

                    {/* Billing Toggle */}
                    <div className="billing-toggle-container">
                        <div className="billing-toggle-wrapper">
                            <span className={`billing-period-label ${billingPeriod === 'monthly' ? 'active' : ''}`}>
                                Monthly
                            </span>
                            <Toggle
                                checked={billingPeriod === 'annually'}
                                onChange={(checked) => setBillingPeriod(checked ? 'annually' : 'monthly')}
                                size="md"
                            />
                            <span className={`billing-period-label ${billingPeriod === 'annually' ? 'active' : ''}`}>
                                Annually <span className="discount">SAVE 17%</span>
                            </span>
                        </div>
                    </div>

                    <div className="pricing-grid">
                        {plans.map((plan) => {
                            const isPopular = plan.popular;
                            const price = plan.prices[billingPeriod];
                            const subText = plan.subTexts[billingPeriod];

                            return (
                                <div
                                    key={plan.name}
                                    className={`pricing-card ${plan.name.toLowerCase()} ${isPopular ? 'featured' : ''}`}
                                >
                                    {isPopular && <div className="popular-badge">Most Popular</div>}

                                    <div className="pricing-header">
                                        <h3>{plan.name}</h3>
                                        <div className="price">
                                            {price.includes('$') ? (
                                                <>
                                                    <span className="currency">$</span>
                                                    <span className="amount">{price.replace('$', '')}</span>
                                                    <span className="period"> USD</span>
                                                </>
                                            ) : (
                                                <span className="contact-sales">{price}</span>
                                            )}
                                        </div>
                                        <div className="price-subtitle">{subText}</div>
                                    </div>

                                    <div className="plan-description">{plan.description}</div>

                                    <ul className="features-list">
                                        {plan.features.map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                    </ul>

                                    {plan.name === 'Enterprise' ? (
                                        <a href="mailto:sales@zoobbe.com" className="pricing-cta">Contact Sales</a>
                                    ) : plan.name === 'Free' ? (
                                        <Link to="/signup" className="pricing-cta">Get Started Free</Link>
                                    ) : (
                                        <Link to="/signup" className="pricing-cta">Start Free Trial</Link>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section id="testimonials" className="testimonials">
                <div className="container">
                    <div className="section-header">
                        <h2>Trusted by Teams Worldwide</h2>
                        <p>See what our users have to say</p>
                    </div>
                    <div className="testimonials-grid">
                        <div className="testimonial-card">
                            <div className="testimonial-content">
                                <p>"Just checked out Zoobbe — clean, fast, and super smooth! 🎯 I really appreciate the simplicity and real-time updates. It feels lightweight yet powerful, kind of like a solid alternative to Trello or ClickUp. Congrats on the launch — excited to see how this grows over time! 🚀"</p>
                            </div>
                            <div className="testimonial-author">
                                <div className="author-avatar">
                                    <span>MK</span>
                                </div>
                                <div className="author-info">
                                    <h4>M A Kuddus Munsi</h4>
                                    <p>Solo Founder, <a href="https://www.linkedin.com/in/dekuddus/" target="_blank" rel="noopener noreferrer">Munsi</a></p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to Transform Your Workflow?</h2>
                        <p>Join thousands of teams already using Zoobbe to stay organized and productive.</p>
                        <div className="cta-actions">
                            <Link to="/signup" className="cta-button primary">Get Started Free</Link>
                            <a href="#how-it-works" onClick={openVideo} className="cta-button secondary">Watch Demo</a>
                        </div>
                        {/* <p className="cta-note">No credit card required • Free forever plan available</p> */}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <StaticFooter />

            {/* Video Popup Modal */}
            {isVideoOpen && (
                <div className="video-modal" onClick={closeVideo}>
                    <div className="video-modal__overlay"></div>
                    <div className="video-modal__content" onClick={(e) => e.stopPropagation()}>
                        <button className="video-modal__close" onClick={closeVideo} aria-label="Close video">
                            <X size={24} />
                        </button>
                        <div className="video-modal__wrapper">
                            <iframe
                                src="https://www.youtube.com/embed/N4unkwwinfU?autoplay=1"
                                title="Zoobbe Demo Video"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;
